import type { EventCategory } from '@mds/shared/types/event';
import type {
  CreateGuestEventRegistrationRequest,
  GuestEvent,
  GuestEventListResponse,
  GuestEventRegistrationReceipt,
  GuestEventRegistrationResponse,
  GuestEventResponse,
} from '@mds/shared/types/guest-event';

import { api } from '@/lib/api';

/** The public events endpoints — see `backend/docs/guest-events.md`. */

/** A type alias, so it carries the index signature the client's `query` needs. */
export type GuestEventQuery = {
  page?: number;
  limit?: number;
  category?: EventCategory;
  pricing?: 'free' | 'paid';
  /** Across title, short description and space. */
  q?: string;
};

/**
 * Not wrapped in `data`: the body is `{ data, page, limit, total, counts }`.
 *
 * An empty search is dropped rather than sent — the server requires `q` to have
 * at least one character, so a cleared search box would otherwise be a 400.
 */
export const listEvents = (query: GuestEventQuery = {}): Promise<GuestEventListResponse> =>
  api.get<GuestEventListResponse>('events', {
    query: { ...query, q: query.q?.trim() || undefined },
  });

export const getEvent = (eventIdentifier: string): Promise<GuestEvent> =>
  api.get<GuestEventResponse>(`events/${eventIdentifier}`).then((r) => r.data);

/**
 * Registers a guest. `requestId` makes a retry idempotent — one UUID per
 * submission, reused only to retry that same submission.
 *
 * The server calculates and snapshots the amount inside the event lock. The
 * client does not submit a price.
 */
export const registerForEvent = (
  eventId: string,
  body: CreateGuestEventRegistrationRequest,
): Promise<GuestEventRegistrationReceipt> =>
  api
    .post<GuestEventRegistrationResponse>(`events/${eventId}/registrations`, body)
    .then((r) => r.data);
